import React from 'react';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { usePersonalInformation } from '@/hooks/usePersonalInformation';
import { useFirstClientCaseId } from '@/hooks/queries/useFirstClientCaseId';
import { useUser } from '@/hooks/useUser';
import { UserI } from '@/interfaces';
import { ErrorHttpResponse } from '@/interfaces/httpClient';
import { useNotification } from '@/hooks';
import useModal from '@/hooks/useModal';
import { ModalHeader } from '@/components/modals/ModalHeader';
import { StepTracker } from '@/components/modals/StepTracker';
import AppRoutes from '@/const/routes';
import { covalyntService } from '@/api';
import { formatPhoneRemove } from '@/lib';
import { Phase, VERIFYING_DURATION_MS } from './types';
import { FormPhaseBody, FormPhaseFooter } from './FormPhase';
import { VerifyingPhaseBody, VerifyingPhaseFooter } from './VerifyingPhase';
import {
	UnsuccessfulPhaseBody,
	UnsuccessfulPhaseFooter,
} from './UnsuccessfulPhase';
import { FailedPhaseBody, FailedPhaseFooter } from './FailedPhase';
import { SuccessfulPhaseBody, SuccessfulPhaseFooter } from './SuccessfulPhase';

const REDIRECT_SECONDS = 15;

interface Props {
	id: string;
	userInformation: UserI | undefined;
	isLoading?: boolean;
	isOpen: boolean;
	setIsOpen: (value: boolean) => void;
	refetch: () => void;
	onContinue?: () => void;
	/** Off for the standalone "Verify Your Identity" action — it isn't part of
	 * the 2-step (verify → upload document) sequence. */
	showStepTracker?: boolean;
	successCtaLabel?: string;
	successDescription?: string;
}

export const IdentityVerificationModal: React.FC<Props> = ({
	id,
	isOpen,
	userInformation,
	isLoading = false,
	refetch,
	onContinue,
	setIsOpen,
	showStepTracker = true,
	successCtaLabel,
	successDescription,
}) => {
	const router = useRouter();
	const { signOut } = useUser();
	const { Modal, show, hide } = useModal();
	const [phase, setPhase] = React.useState<Phase>('form');
	const [verifyingStepIndex, setVerifyingStepIndex] = React.useState(0);
	const [verifyingPercent, setVerifyingPercent] = React.useState(0);
	const [redirectSeconds, setRedirectSeconds] =
		React.useState(REDIRECT_SECONDS);

	const { rules, isVerified, initialPhone, form } = usePersonalInformation({
		id,
		userInformation,
		requireAddress: true,
	});
	const { caseId } = useFirstClientCaseId();
	const { toastError } = useNotification();
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const { register, watch, handleSubmit } = form;

	const isConfirmed = watch('valid');
	const showLoader = isLoading && !userInformation;

	React.useEffect(() => {
		if (isOpen) {
			show();
			setPhase('form');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	// Cosmetic checklist animation for the verifying phase — not tied to the
	// real check, which doesn't exist yet. Spaced evenly across the full
	// verifying duration so it doesn't rush ahead of the (mock) result.
	React.useEffect(() => {
		if (phase !== 'verifying') return;
		setVerifyingStepIndex(0);
		const t1 = setTimeout(
			() => setVerifyingStepIndex(1),
			VERIFYING_DURATION_MS / 3
		);
		const t2 = setTimeout(
			() => setVerifyingStepIndex(2),
			(VERIFYING_DURATION_MS / 3) * 2
		);
		return () => {
			clearTimeout(t1);
			clearTimeout(t2);
		};
	}, [phase]);

	// Continuous ring progress — ticks smoothly for the full duration instead
	// of jumping in 3 discrete steps.
	React.useEffect(() => {
		if (phase !== 'verifying') return;
		setVerifyingPercent(0);
		const start = Date.now();
		const interval = setInterval(() => {
			const elapsed = Date.now() - start;
			setVerifyingPercent(
				Math.min(100, (elapsed / VERIFYING_DURATION_MS) * 100)
			);
		}, 50);
		return () => clearInterval(interval);
	}, [phase]);

	// Countdown + sign out + redirect to sign-in once verification is marked failed.
	React.useEffect(() => {
		if (phase !== 'failed') return;
		setRedirectSeconds(REDIRECT_SECONDS);
		const interval = setInterval(() => {
			setRedirectSeconds((prev) => {
				if (prev <= 1) {
					clearInterval(interval);
					signOut().finally(() => {
						router.push(AppRoutes.CLIENTAPP_AUTH_SIGN_IN);
					});
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
		return () => clearInterval(interval);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [phase, router]);

	const handleContinue = handleSubmit(async (data) => {
		// Stay on the form until the backend answers: some errors (e.g. phone
		// already in use) must be fixed in the form, not shown as a failed attempt.
		setIsSubmitting(true);
		let nextPhase: Phase;
		try {
			const response = await covalyntService.verifyIdentity({
				caseId: caseId ?? '',
				firstName: data.firstName,
				lastName: data.lastName,
				phoneNumber: formatPhoneRemove(data.phone),
				address1: data.address,
				address2: data.address2 || null,
				city: data.cityName,
				state: data.stateName,
				zipCode: data.zipCode,
			});
			const { passed, attempts } = response.data;
			nextPhase = passed
				? 'successful'
				: attempts >= 2
				? 'failed'
				: 'unsuccessful';
		} catch (error) {
			const { status, message } = error as ErrorHttpResponse;
			if (
				status === 400 &&
				typeof message === 'string' &&
				message.toLowerCase().includes('phone number is already in use')
			) {
				toastError('This phone number is already in use by another user.');
				form.setError(
					'phone',
					{ type: 'manual', message: 'This phone number is already in use.' },
					{ shouldFocus: true }
				);
				setIsSubmitting(false);
				return;
			}
			// eslint-disable-next-line no-console
			console.log('[covalynt/verify-identity] error', error);
			nextPhase = 'unsuccessful';
		}

		setIsSubmitting(false);
		setPhase('verifying');
		// Play the verifying animation in full before revealing the result.
		await new Promise((resolve) => setTimeout(resolve, VERIFYING_DURATION_MS));
		setPhase(nextPhase);
	});

	const handleTryAgain = () => setPhase('form');

	const handleClose = () => {
		hide();
		setIsOpen(false);
		if (phase === 'successful') {
			refetch();
		}
		setPhase('form');
	};

	const handleFinish = () => {
		hide();
		setIsOpen(false);
		onContinue?.();
	};

	return (
		<Modal
			withXButton={
				phase === 'form' || phase === 'unsuccessful' || phase === 'successful'
			}
			avoidClose={phase === 'verifying' || phase === 'failed'}
			title={
				<div className="flex flex-col md:flex-row md:items-center md:justify-between items-start gap-2 w-full">
					<ModalHeader
						title="Identity Verification"
						classNameContent="!justify-start !w-auto"
						classNameImage="!block"
						classNameTitle="text-left"
					/>
					{showStepTracker && (
						<StepTracker
							currentStep={1}
							totalSteps={2}
							className="self-end md:self-center"
						/>
					)}
				</div>
			}
			classNameContainerChildren={clsx('flex flex-col', {
				'justify-center': phase !== 'form',
			})}
			classNameFooter="border-t border-gray-100"
			onClose={handleClose}
			classNameCardContainer="!bg-gradient-to-r from-white to-white md:w-full max-w-[900px] max-h-[90vh] flex flex-col relative"
			footer={
				phase === 'verifying' ? (
					<VerifyingPhaseFooter />
				) : phase === 'unsuccessful' ? (
					<UnsuccessfulPhaseFooter
						onClose={handleClose}
						onTryAgain={handleTryAgain}
					/>
				) : phase === 'failed' ? (
					<FailedPhaseFooter redirectSeconds={redirectSeconds} />
				) : phase === 'successful' ? (
					<SuccessfulPhaseFooter
						onContinue={handleFinish}
						label={successCtaLabel}
					/>
				) : showLoader ? undefined : (
					<FormPhaseFooter
						register={register}
						isConfirmed={isConfirmed}
						onClose={handleClose}
						onContinue={handleContinue}
						isSubmitting={isSubmitting}
					/>
				)
			}
		>
			{phase === 'verifying' ? (
				<VerifyingPhaseBody
					stepIndex={verifyingStepIndex}
					percent={verifyingPercent}
				/>
			) : phase === 'unsuccessful' ? (
				<UnsuccessfulPhaseBody />
			) : phase === 'failed' ? (
				<FailedPhaseBody />
			) : phase === 'successful' ? (
				<SuccessfulPhaseBody description={successDescription} />
			) : (
				<FormPhaseBody
					form={form}
					rules={rules}
					userInformation={userInformation}
					isVerified={isVerified}
					initialPhone={initialPhone}
					refetch={refetch}
					showLoader={showLoader}
				/>
			)}
		</Modal>
	);
};
